import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { SEO } from '@/components/SEO';
import { PublicHeader } from '@/components/marketing/PublicHeader';
import { PublicFooter } from '@/components/marketing/PublicFooter';
import { PortableTextRenderer } from '@/components/insights/PortableTextRenderer';
import { InsightCard } from '@/components/insights/InsightCard';
import { InsightPostSkeleton } from '@/components/insights/InsightsSkeleton';
import { useInsightPost, useRelatedPosts } from '@/hooks/useInsights';
import { urlFor } from '@/lib/sanity';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, ArrowRight, Clock, Calendar } from 'lucide-react';

export default function InsightPost() {
  const { slug } = useParams<{ slug: string }>();
  
  const { data: post, isLoading, error } = useInsightPost(slug || '');
  
  const categoryIds = post?.categories?.map((c) => c._id) || [];
  const { data: relatedPosts } = useRelatedPosts(slug || '', categoryIds);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        <main className="flex-1 py-12">
          <div className="container">
            <InsightPostSkeleton />
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        <main className="flex-1 py-24">
          <div className="container text-center">
            <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The post you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild>
              <Link to="/insights">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Insights
              </Link>
            </Button>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  const readingTime = post.estimatedReadingTime || 5;
  const publishDate = new Date(post.publishedAt);
  
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.seoDescription || post.excerpt || '',
    image: post.mainImage ? urlFor(post.mainImage).width(1200).height(630).url() : undefined,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: post.author ? {
      '@type': 'Person',
      name: post.author.name,
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: 'Radius',
      logo: {
        '@type': 'ImageObject',
        url: 'https://useradius.io/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://useradius.io/insights/${slug}`,
    },
  };

  return (
    <>
      <SEO
        title={`${post.seoTitle || post.title} | Radius Insights`}
        description={post.seoDescription || post.excerpt || `Read ${post.title} on Radius Insights`}
        canonical={`/insights/${slug}`}
        type="article"
        image={post.mainImage ? urlFor(post.mainImage).width(1200).height(630).url() : undefined}
        structuredData={structuredData}
      />
      
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        
        <main className="flex-1">
          <article className="py-12 md:py-16">
            <div className="container">
              <div className="max-w-3xl mx-auto">
                {/* Back Link */}
                <Link 
                  to="/insights" 
                  className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Insights
                </Link>
                
                {/* Header */}
                <header className="space-y-6 mb-8">
                  {/* Categories */}
                  {post.categories && post.categories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.categories.map((category) => (
                        <Badge key={category._id} variant="secondary">
                          {category.title}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Title */}
                  <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                    {post.title}
                  </h1>
                  
                  {/* Excerpt */}
                  {post.excerpt && (
                    <p className="text-lg text-muted-foreground">
                      {post.excerpt}
                    </p>
                  )}
                  
                  {/* Author & Meta */}
                  <div className="flex items-center justify-between py-4 border-y border-border">
                    {post.author && (
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          {post.author.image && (
                            <AvatarImage 
                              src={urlFor(post.author.image).width(80).height(80).url()} 
                              alt={post.author.name} 
                            />
                          )}
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {post.author.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">{post.author.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(publishDate, 'MMMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(publishDate, 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {readingTime} min read
                      </span>
                    </div>
                  </div>
                </header>
                
                {/* Featured Image */}
                {post.mainImage && (
                  <figure className="mb-10">
                    <img
                      src={urlFor(post.mainImage).width(800).url()}
                      alt={post.title}
                      className="w-full rounded-lg"
                    />
                  </figure>
                )}
                
                {/* Body Content */}
                <div className="prose prose-lg max-w-none">
                  {post.body && <PortableTextRenderer value={post.body} />}
                </div>
                
                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mt-10 pt-6 border-t border-border">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <Badge key={tag._id} variant="outline">
                          #{tag.title}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </article>
          
          {/* Related Posts */}
          {relatedPosts && relatedPosts.length > 0 && (
            <section className="py-12 md:py-16 bg-muted/30 border-t">
              <div className="container">
                <h2 className="text-2xl font-bold mb-8 text-center">Related Insights</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {relatedPosts.map((relatedPost) => (
                    <InsightCard key={relatedPost._id} post={relatedPost} />
                  ))}
                </div>
              </div>
            </section>
          )}
          
          {/* CTA Section */}
          <section className="py-16 md:py-24 bg-primary/5 border-t">
            <div className="container">
              <div className="max-w-3xl mx-auto text-center space-y-6">
                <h2 className="text-3xl font-bold text-foreground">
                  Ready to Streamline Your Procurement?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Join thousands of businesses using Radius to discover vendors with transparent profiles 
                  and streamline their sourcing process.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <Link to="/auth/signup">
                      Get Started Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/insights">View All Insights</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        
        <PublicFooter />
      </div>
    </>
  );
}
